/**
 * Poker Lobby - Browse and join multiplayer tables
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, RefreshCw, Plus, Users, Play, Clock, 
  Wifi, WifiOff, Crown, ChevronRight, X, Copy, Check
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { BOOSTY_LOGO_URL } from './pokerConstants.js';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PokerLobby = () => {
  const navigate = useNavigate();
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [playerName, setPlayerName] = useState('');
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  
  // Create table settings
  const [newTableSettings, setNewTableSettings] = useState({
    gameType: 'holdem',
    maxPlayers: 6
  });

  const fetchTables = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_URL}/api/poker/tables`);
      if (!response.ok) throw new Error('Failed to fetch tables');
      const data = await response.json();
      setTables(data.tables || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTables();
    // Auto-refresh every 10 seconds
    const interval = setInterval(fetchTables, 10000);
    return () => clearInterval(interval);
  }, [fetchTables]);

  const createTable = async () => {
    try {
      setCreating(true);
      const response = await fetch(
        `${API_URL}/api/poker/tables/create?game_type=${newTableSettings.gameType}&max_players=${newTableSettings.maxPlayers}`,
        { method: 'POST' }
      );
      if (!response.ok) throw new Error('Failed to create table');
      const data = await response.json();
      
      // Join the newly created table
      setSelectedTable({ id: data.table_id, game_type: newTableSettings.gameType, max_players: newTableSettings.maxPlayers, players: 0 });
      setShowCreateModal(false);
      setShowJoinModal(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const joinTable = (table) => {
    setSelectedTable(table);
    setShowJoinModal(true);
  };

  const confirmJoin = () => {
    if (!playerName.trim()) {
      setPlayerName('Player_' + Math.random().toString(36).substring(2, 6));
    }
    // Navigate to the game with table info
    navigate(`/games/poker/multiplayer/${selectedTable.id}`, {
      state: { playerName: playerName.trim() || 'Player_' + Math.random().toString(36).substring(2, 6), tableId: selectedTable.id }
    });
  };

  const copyTableId = (tableId) => {
    navigator.clipboard.writeText(tableId);
    setCopiedId(tableId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getPhaseColor = (phase) => {
    switch (phase) {
      case 'waiting': return 'text-green-400 bg-green-500/20';
      case 'preflop': 
      case 'flop':
      case 'turn':
      case 'river': return 'text-amber-400 bg-amber-500/20';
      case 'showdown': return 'text-red-400 bg-red-500/20';
      default: return 'text-slate-400 bg-slate-500/20';
    }
  };

  const getPhaseLabel = (phase) => {
    switch (phase) {
      case 'waiting': return 'Open';
      case 'preflop': return 'Pre-Flop';
      case 'flop': return 'Flop';
      case 'turn': return 'Turn';
      case 'river': return 'River';
      case 'showdown': return 'Showdown';
      default: return phase;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-emerald-950/30 to-slate-950">
      {/* Header */}
      <div className="bg-slate-900/80 backdrop-blur-sm border-b border-slate-700/50 px-4 py-3">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/games/poker')} className="text-slate-400 hover:text-white" data-testid="back-to-poker-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Poker Room
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchTables}
              disabled={loading}
              className="border-slate-600 text-slate-300"
              data-testid="refresh-tables-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button 
              size="sm" 
              onClick={() => setShowCreateModal(true)}
              className="bg-emerald-600 hover:bg-emerald-500"
              data-testid="create-table-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Table
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-6">
        {/* Title */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-3">
            <Users className="w-8 h-8 text-emerald-400" />
            <h1 className="text-3xl font-bold text-white">Multiplayer Lobby</h1>
          </div>
          <p className="text-slate-400">Join a table or create your own</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 mb-6 text-center">
            <p className="text-red-400">{error}</p>
            <Button variant="ghost" size="sm" onClick={fetchTables} className="mt-2 text-red-300">
              Try Again
            </Button>
          </div>
        )}

        {/* Tables List */}
        <div className="space-y-4">
          {loading && tables.length === 0 ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 text-slate-500 animate-spin mx-auto mb-4" />
              <p className="text-slate-400">Loading tables...</p>
            </div>
          ) : tables.length === 0 ? (
            <div className="bg-slate-900/50 rounded-xl border border-slate-700/50 p-12 text-center">
              <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No Tables Available</h3>
              <p className="text-slate-400 mb-6">Be the first to create a table!</p>
              <Button 
                onClick={() => setShowCreateModal(true)}
                className="bg-emerald-600 hover:bg-emerald-500"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Table
              </Button>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white">
                  Available Tables ({tables.length})
                </h2>
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  Live
                </div>
              </div>
              
              {tables.map((table, idx) => (
                <motion.div
                  key={table.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="bg-slate-900/80 backdrop-blur-sm rounded-xl border border-slate-700/50 hover:border-emerald-500/50 transition-all overflow-hidden"
                  data-testid={`table-${table.id}`}
                >
                  <div className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {/* Table Icon */}
                      <div className="w-14 h-14 rounded-xl bg-emerald-600/20 flex items-center justify-center">
                        <img src={BOOSTY_LOGO_URL} alt="" className="h-10 w-auto opacity-80" />
                      </div>
                      
                      {/* Table Info */}
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-white">
                            {table.game_type === 'holdem' ? "Texas Hold'em" : 'Three Card Poker'}
                          </h3>
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${getPhaseColor(table.phase)}`}>
                            {getPhaseLabel(table.phase)}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-4 text-sm text-slate-400">
                          <div className="flex items-center gap-1">
                            <Users className="w-3.5 h-3.5" />
                            <span>{table.players}/{table.max_players} players</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Crown className="w-3.5 h-3.5 text-amber-400" />
                            <span>Blinds 5/10</span>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 mt-1">
                          <code className="text-[10px] text-slate-500 bg-slate-800 px-1.5 py-0.5 rounded">
                            {table.id}
                          </code>
                          <button 
                            onClick={() => copyTableId(table.id)}
                            className="text-slate-500 hover:text-white transition-colors"
                          >
                            {copiedId === table.id ? (
                              <Check className="w-3 h-3 text-green-400" />
                            ) : (
                              <Copy className="w-3 h-3" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Join Button */}
                    <div className="flex items-center gap-3">
                      {table.players < table.max_players ? (
                        <Button
                          onClick={() => joinTable(table)}
                          className="bg-emerald-600 hover:bg-emerald-500"
                          data-testid={`join-table-${table.id}-btn`}
                        >
                          <Play className="w-4 h-4 mr-2" />
                          Join
                        </Button>
                      ) : (
                        <Button disabled variant="outline" className="border-slate-600 text-slate-500">
                          Full
                        </Button>
                      )}
                      <ChevronRight className="w-5 h-5 text-slate-600" />
                    </div>
                  </div>
                  
                  {/* Player Slots */}
                  <div className="bg-slate-800/50 px-4 py-2 border-t border-slate-700/50">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">Seats:</span>
                      {Array.from({ length: table.max_players }).map((_, i) => (
                        <div 
                          key={i}
                          className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                            i < table.players 
                              ? 'bg-emerald-500/30 text-emerald-400 border border-emerald-500/50' 
                              : 'bg-slate-700/50 text-slate-500 border border-slate-600/50'
                          }`}
                        >
                          {i + 1}
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </>
          )}
        </div>

        {/* Quick Join by Code */}
        <div className="mt-8 bg-slate-900/50 rounded-xl border border-slate-700/50 p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Join by Table Code</h3>
          <div className="flex gap-3">
            <input
              type="text"
              placeholder="Enter table code (e.g., table_12345)"
              className="flex-1 bg-slate-800 border border-slate-600 rounded-lg px-4 py-2 text-white placeholder-slate-500 focus:border-emerald-500 focus:outline-none"
              data-testid="table-code-input"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.target.value) {
                  joinTable({ id: e.target.value, game_type: 'holdem', max_players: 6, players: 0 });
                }
              }}
            />
            <Button 
              variant="outline" 
              className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/20"
              onClick={(e) => {
                const input = e.target.closest('div').querySelector('input');
                if (input.value) {
                  joinTable({ id: input.value, game_type: 'holdem', max_players: 6, players: 0 });
                }
              }}
              data-testid="join-by-code-btn"
            >
              Join
            </Button>
          </div>
        </div>
      </div>

      {/* Create Table Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-900 border border-slate-700 rounded-2xl p-6 w-full max-w-md"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Create Table</h2>
                <button onClick={() => setShowCreateModal(false)} className="text-slate-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Game Type */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Game Type</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setNewTableSettings(s => ({ ...s, gameType: 'holdem' }))}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        newTableSettings.gameType === 'holdem'
                          ? 'border-emerald-500 bg-emerald-500/20 text-white'
                          : 'border-slate-600 text-slate-400 hover:border-slate-500'
                      }`}
                    >
                      <div className="font-semibold">Texas Hold'em</div>
                      <div className="text-xs text-slate-500">2 hole + 5 community</div>
                    </button>
                    <button
                      onClick={() => setNewTableSettings(s => ({ ...s, gameType: 'three_card' }))}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        newTableSettings.gameType === 'three_card'
                          ? 'border-emerald-500 bg-emerald-500/20 text-white'
                          : 'border-slate-600 text-slate-400 hover:border-slate-500'
                      }`}
                    >
                      <div className="font-semibold">Three Card</div>
                      <div className="text-xs text-slate-500">3 cards, fast action</div>
                    </button>
                  </div>
                </div>

                {/* Max Players */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Table Size</label>
                  <div className="grid grid-cols-3 gap-3">
                    {[2, 4, 6].map(num => (
                      <button
                        key={num}
                        onClick={() => setNewTableSettings(s => ({ ...s, maxPlayers: num }))}
                        className={`p-3 rounded-lg border text-center transition-all ${
                          newTableSettings.maxPlayers === num
                            ? 'border-emerald-500 bg-emerald-500/20 text-white'
                            : 'border-slate-600 text-slate-400 hover:border-slate-500'
                        }`}
                      >
                        <div className="font-bold text-lg">{num}</div>
                        <div className="text-xs text-slate-500">
                          {num === 2 ? 'Heads Up' : num === 4 ? '4-Max' : '6-Max'}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Blinds Info */}
                <div className="bg-slate-800/50 rounded-lg p-3 text-sm text-slate-400">
                  <div className="flex items-center gap-2">
                    <Crown className="w-4 h-4 text-amber-400" />
                    <span>Blinds: 5/10 | Starting chips: 1,000</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 border-slate-600"
                >
                  Cancel
                </Button>
                <Button
                  onClick={createTable}
                  disabled={creating}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-500"
                  data-testid="confirm-create-table-btn"
                >
                  {creating ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Create & Join
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Join Table Modal */}
      <AnimatePresence>
        {showJoinModal && selectedTable && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => setShowJoinModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-900 border border-slate-700 rounded-2xl p-6 w-full max-w-md"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Join Table</h2>
                <button onClick={() => setShowJoinModal(false)} className="text-slate-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Table Info */}
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-emerald-600/20 flex items-center justify-center">
                      <Users className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <div className="font-semibold text-white">
                        {selectedTable.game_type === 'holdem' ? "Texas Hold'em" : 'Three Card Poker'}
                      </div>
                      <code className="text-xs text-slate-500">{selectedTable.id}</code>
                    </div>
                  </div>
                </div>

                {/* Player Name */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Your Name</label>
                  <input
                    type="text"
                    value={playerName}
                    onChange={(e) => setPlayerName(e.target.value)}
                    placeholder="Enter your display name"
                    className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:border-emerald-500 focus:outline-none"
                    data-testid="player-name-input"
                    maxLength={20}
                  />
                  <p className="text-xs text-slate-500 mt-1">This is how other players will see you</p>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setShowJoinModal(false)}
                  className="flex-1 border-slate-600"
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmJoin}
                  disabled={joining}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-500"
                  data-testid="confirm-join-btn"
                >
                  {joining ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Joining...
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4 mr-2" />
                      Join Table
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PokerLobby;
